<template>
  <div>
    <div class="mb-1">
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        @click="voltar"
      >
        <feather-icon
          icon="ArrowLeftIcon"
          size="14"
          class="mr-25"
        />
        {{ $t('equipment.types.backButton') }}
      </button>
    </div>

    <div class="row">
      <div class="col-md-5">
        <b-card no-body>
          <div class="p-1 d-flex align-items-center justify-content-between">
            <h6 class="mb-0">
              {{ $t('equipment.types.unitsTitle') }}
            </h6>
            <small class="text-muted">{{ $t('equipment.types.unitsPredefined') }}</small>
          </div>
          <table class="table table-sm mb-0">
            <thead>
              <tr>
                <th>{{ $t('equipment.types.nameColumn') }}</th>
                <th>{{ $t('equipment.types.abbreviationColumn') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="unidades.length === 0">
                <td
                  colspan="2"
                  class="text-center text-muted py-3"
                >
                  {{ $t('equipment.types.noUnits') }}
                </td>
              </tr>
              <tr
                v-for="u in unidades"
                :key="u.id"
              >
                <td>{{ u.name }}</td>
                <td><span class="badge badge-secondary">{{ u.abbreviation }}</span></td>
              </tr>
            </tbody>
          </table>
        </b-card>
      </div>

      <div class="col-md-7">
        <b-card no-body>
          <div class="p-1 d-flex align-items-center justify-content-between">
            <h6 class="mb-0">
              {{ $t('equipment.types.typesTitle') }}
            </h6>
            <button
              class="btn btn-sm btn-success"
              :disabled="unidades.length === 0"
              @click="abrirTipo()"
            >
              {{ $t('equipment.types.newButton') }}
            </button>
          </div>
          <table class="table table-sm mb-0">
            <thead>
              <tr>
                <th>{{ $t('equipment.types.nameColumn') }}</th>
                <th>{{ $t('equipment.types.measureInColumn') }}</th>
                <th class="text-right">
                  {{ $t('common.actions') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="tipos.length === 0">
                <td
                  colspan="3"
                  class="text-center text-muted py-3"
                >
                  {{ unidades.length === 0 ? $t('equipment.types.registerUnitFirst') : $t('equipment.types.noTypes') }}
                </td>
              </tr>
              <tr
                v-for="t in tipos"
                :key="t.id"
              >
                <td>
                  {{ t.name }}
                  <small
                    v-if="t.description"
                    class="text-muted d-block"
                  >{{ t.description }}</small>
                </td>
                <td>{{ t.unit ? `${t.unit.name} (${t.unit.abbreviation})` : '—' }}</td>
                <td class="text-right">
                  <button
                    class="btn btn-sm btn-outline-secondary"
                    @click="abrirTipo(t)"
                  >
                    {{ $t('common.edit') }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </b-card>
      </div>

      <b-modal
        v-model="mostrarTipo"
        :title="tipo.id ? $t('equipment.types.editTypeTitle') : $t('equipment.types.newTypeTitle')"
        hide-footer
      >
        <div class="form-group">
          <label>{{ $t('equipment.types.nameColumn') }}</label>
          <input
            v-model="tipo.name"
            type="text"
            class="form-control"
            maxlength="120"
            :placeholder="$t('equipment.types.namePlaceholder')"
          >
        </div>
        <div class="form-group">
          <label>{{ $t('equipment.types.measureInColumn') }}</label>
          <v-select
            v-model="tipo.unit_id"
            :options="unidades"
            :reduce="u => u.id"
            label="name"
          />
          <small class="text-muted">
            {{ $t('equipment.types.unitHint') }}
          </small>
        </div>
        <div class="form-group">
          <label>{{ $t('equipment.types.descriptionLabel') }}</label>
          <input
            v-model="tipo.description"
            type="text"
            class="form-control"
            maxlength="255"
          >
        </div>
        <div class="text-right">
          <button
            class="btn btn-secondary mr-1"
            @click="mostrarTipo = false"
          >
            {{ $t('common.cancel') }}
          </button>
          <button
            class="btn btn-primary"
            :disabled="salvando"
            @click="salvarTipo"
          >
            {{ $t('common.save') }}
          </button>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import ApiErrorMixin from '@/mixins/ApiErrorMixin'

export default {
  name: 'EquipmentTypes',
  mixins: [ApiErrorMixin],

  data() {
    return {
      unidades: [],
      tipos: [],
      salvando: false,
      mostrarTipo: false,
      tipo: {
        id: null, name: '', unit_id: null, description: '',
      },
    }
  },

  mounted() {
    this.carregar()
  },

  methods: {
    voltar() {
      const nodeId = this.$route.query.node_id

      if (nodeId) {
        this.$router.push({ name: 'AdminNode', params: { nodeId } })
        return
      }

      this.$router.back()
    },

    async carregar() {
      try {
        const [u, t] = await Promise.all([
          this.$http.get(`${this.$store.state.api}equipment/units`, { params: { all: 1 } }),
          this.$http.get(`${this.$store.state.api}equipment/types`, { params: { all: 1 } }),
        ])
        this.unidades = u.data.data || []
        this.tipos = t.data.data || []
      } catch (e) {
        this.$message(this.$t('equipment.common.errorTitle'), this.apiErrorText(e, this.$t('equipment.types.loadError')), 'error')
      }
    },

    abrirTipo(t) {
      this.tipo = t
        ? {
          id: t.id, name: t.name, unit_id: t.unit_id, description: t.description || '',
        }
        : {
          id: null, name: '', unit_id: null, description: '',
        }
      this.mostrarTipo = true
    },

    async salvarTipo() {
      this.salvando = true
      try {
        const base = `${this.$store.state.api}equipment/types`
        const corpo = {
          name: this.tipo.name,
          unit_id: this.tipo.unit_id,
          description: this.tipo.description,
        }

        if (this.tipo.id) await this.$http.put(`${base}/${this.tipo.id}`, corpo)
        else await this.$http.post(base, corpo)

        this.mostrarTipo = false
        this.carregar()
      } catch (e) {
        this.$message(this.$t('equipment.common.errorTitle'), this.apiErrorText(e, this.$t('equipment.types.saveError')), 'error')
      } finally {
        this.salvando = false
      }
    },
  },
}
</script>
